import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Sparkline from "../components/Sparkline";
import { ChevronLeft, ChevronRight, Calendar, TrendingUp, RefreshCw, ArrowLeft } from "lucide-react";

const fmtDelta = (cur, prev) => {
  if (prev === 0 && cur === 0) return { text: "flat", color: "text-muted-foreground" };
  if (prev === 0) return { text: "new", color: "text-accent" };
  const diff = cur - prev;
  if (diff === 0) return { text: "±0%", color: "text-muted-foreground" };
  const pct = Math.round(100 * diff / prev);
  return { text: `${diff > 0 ? "▲" : "▼"} ${Math.abs(pct)}%`, color: diff > 0 ? "text-green-600" : "text-destructive" };
};

export default function WeeklyRecap() {
  const { brandName } = useParams();
  const brand = brandName ? decodeURIComponent(brandName) : null;

  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { offset };
      if (brand) params.brand = brand;
      const [cur, prev, tr] = await Promise.all([
        api.get("/analytics/recap-detail", { params }),
        api.get("/analytics/recap-detail", { params: { ...params, offset: offset + 1 } }).catch(() => ({ data: null })),
        offset === 0 && brand
          ? api.get("/analytics/pillar-trend", { params: { brand, weeks: 6 } }).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setData(cur.data);
      setPrevData(prev?.data || null);
      setTrend(tr?.data || null);
    } catch (e) {
      toast.error(errText(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [offset, brand]);

  // Keyboard shortcuts: ←/→ jump between adjacent recap windows.
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      if (e.key === "ArrowLeft") setOffset((o) => o + 1);
      else if (e.key === "ArrowRight") setOffset((o) => Math.max(0, o - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading && !data) {
    return <div className="max-w-5xl mx-auto p-6"><Card><CardContent className="p-8 text-sm text-muted-foreground">Loading recap…</CardContent></Card></div>;
  }
  if (!data && !loading) {
    return <div className="max-w-5xl mx-auto p-6"><Card><CardContent className="p-8">No data</CardContent></Card></div>;
  }

  const gran = data?.granularity || "week";
  const label = data?.label || "…";
  const prev = prevData || {};

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="weekly-recap-page">
      <div>
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft size={12} /> Dashboard
        </Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              {brand ? brand : "All brands"} · {gran === "week" ? "Weekly recap" : "Monthly recap"}
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold flex items-center gap-2 mt-1">
              <Calendar size={22} /> {label}
            </h1>
            {gran === "month" && (
              <p className="text-xs text-muted-foreground mt-1">
                Weekly recaps go back 3 months, then roll up into monthly summaries.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setOffset(offset + 1)}
                    disabled={loading} data-testid="recap-prev-btn"
                    title={offset >= 12 ? "Older monthly rollup" : "Previous week"}>
              <ChevronLeft size={14} className="mr-1" /> {offset >= 12 ? "Older month" : "Previous week"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setOffset(Math.max(0, offset - 1))}
                    disabled={offset === 0 || loading} data-testid="recap-next-btn">
              {offset === 13 ? "Latest week" : "Newer"} <ChevronRight size={14} className="ml-1" />
            </Button>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading}
                    data-testid="recap-refresh-btn" title="Refresh">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <Card data-testid="recap-stats-card">
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ["Posts", data?.total_posts ?? 0, prev.total_posts ?? 0],
              ["Likes", data?.total_likes ?? 0, prev.total_likes ?? 0],
              ["Comments", data?.total_comments ?? 0, prev.total_comments ?? 0],
              ["Score", data?.total_engagement ?? 0, prev.total_engagement ?? 0],
            ].map(([k, v, p]) => {
              const d = fmtDelta(v, p);
              return (
                <div key={k} className="rounded-lg border p-3 text-center bg-muted/30">
                  <div className="text-3xl font-heading font-semibold">{v}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{k}</div>
                  <div className={`text-[11px] font-semibold mt-1 ${d.color}`}>{d.text}</div>
                </div>
              );
            })}
          </div>
          {(data?.top_pillars?.length || data?.top_companions?.length) && (
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              {data.top_pillars?.[0] && (
                <span className="rounded-md bg-background border px-2 py-1"><b>Top pillar:</b> <span className="capitalize">{data.top_pillars[0].name}</span></span>
              )}
              {data.top_companions?.[0] && (
                <span className="rounded-md bg-background border px-2 py-1"><b>Top companion:</b> {data.top_companions[0].name}</span>
              )}
              {!data.has_engagement_data && (
                <Badge variant="outline" className="text-[10px]">Ranked by volume · no engagement data yet</Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI summary (weekly only) */}
      {gran === "week" && data?.ai_summary && (
        <Card data-testid="recap-ai-card">
          <CardHeader><CardTitle className="text-base">How the week went</CardTitle></CardHeader>
          <CardContent className="text-sm whitespace-pre-line leading-relaxed pt-0">{data.ai_summary}</CardContent>
        </Card>
      )}

      {/* Pillar trend (only when a brand is selected + on the latest week) */}
      {brand && trend?.series?.length > 0 && (
        <Card data-testid="recap-trend-card">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp size={14} /> 6-week pillar trend</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-2">
            {(trend.pillars || []).map((p) => {
              const values = trend.series.map((w) => (w.pillars?.[p]?.score || w.pillars?.[p]?.posts || 0));
              if (!values.some((v) => v)) return null;
              return (
                <div key={p} className="flex items-center justify-between border-b border-dashed py-2">
                  <span className="text-xs capitalize">{p}</span>
                  <Sparkline values={values} width={220} height={26} stroke="var(--accent, #c69d56)" fill="var(--accent, #c69d56)" />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Top posts */}
      <Card data-testid="recap-top-posts-card">
        <CardHeader><CardTitle className="text-base">Top posts</CardTitle></CardHeader>
        <CardContent className="pt-0">
          {(data?.top_posts || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No posts in this window.</p>
          ) : (
            <div className="space-y-2">
              {data.top_posts.map((tp) => (
                <div key={tp.id} className="flex items-center justify-between gap-3 border-b border-dashed py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{tp.title}</div>
                    <div className="text-[11px] text-muted-foreground capitalize">
                      {tp.platform}{tp.coach_name ? ` · ${tp.coach_name}` : ""}
                    </div>
                  </div>
                  <div className="text-xs whitespace-nowrap">
                    <span className="font-mono font-semibold">score {tp.score}</span>
                    <span className="text-muted-foreground"> · {tp.likes}L · {tp.comments}C</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">
        Currently viewing {gran === "week" ? `week ${offset}` : "monthly rollup"}. Use ◀ Previous week to scroll back — or press ← / → on your keyboard.
      </p>
    </div>
  );
}
